/**
 * Crew & Organization entities
 */

import { BaseEntity } from './base.entities';
import { Team } from './team.entities';

export type CrewTier = 'free' | 'pro' | 'enterprise';
export type CrewType = 'corporate' | 'hobby';

export interface CrewTierLimits {
  minMonthlyCost: number;
  maxMembers: number;
  ppm: number; // price per member per month in cents
  priceId?: string; // Stripe Price ID for this tier (optional, used for Stripe integration)
}

// NOTE: what each tier includes is now defined in one place — see
// CREW_MODULE_REGISTRY in ./crewModules.entities.ts (each module's `tiers`/
// `crewTypes` fields). The old ALL_CREW_FEATURES / per-tier `.features`
// string arrays were descriptive-only (never enforced) and have been
// removed in favor of that single source of truth, used by both pricing
// display (PricingPage.tsx) and actual server-side enforcement.

export const CREW_TIER_LIMITS: Record<CrewTier, CrewTierLimits> = {
  free: {
    minMonthlyCost: 0,
    maxMembers: 10,
    ppm: 0
  },
  pro: {
    minMonthlyCost: 0,
    maxMembers: Infinity,
    ppm: 800
  },
  enterprise: {
    minMonthlyCost: 250 * 600, // essentially minimum 250 members
    maxMembers: Infinity,
    ppm: 600
  }
};

export interface Crew extends BaseEntity {
  name: string;
  slug: string;
  description: string;
  teams: Partial<Team>[];
  status: 'active' | 'inactive' | 'deleting' | 'suspended' | 'archived';
  suspendedAt?: Date;
  goals: string[];
  createdByUserGuid?: string; // User GUID of the creator (optional for legacy crews)
  emailDomain?: string;
  ssoProvider?: 'google';
  isPublic?: boolean;
  autoJoin?: boolean;
  allowMemberCreatedTeams?: boolean; // Whether non-admin members can create teams
  headerImage?: string;
  headerImageDimensions?: { width: number; height: number }; // Cached dimensions for placeholder
  tier: CrewTier;
  slackTeamId?: string; // Slack workspace/enterprise ID for deep linking
  websiteUrl?: string; // Optional URL for the organization's website
  communityLinks?: {
    slack?: string; // Slack workspace invite URL
    discord?: string; // Discord server invite URL
    github?: string; // GitHub organization URL
    reddit?: string; // Subreddit URL (e.g., r/mysubreddit)
  };
  enabledProfileLinks?: {
    slack?: boolean;
    discord?: boolean;
    github?: boolean;
    reddit?: boolean;
  };
  crewType: CrewType; // Immutable after creation
  quesoSettings?: {
    assessmentValidityPeriodDays?: number; // How long assessments remain valid (default 365)
    assessmentReminderDays?: number; // Days before expiry to show warnings (default 30)
  };
  badgeSettings?: {
    autoCalculateEligibility?: boolean; // true = calculate eligibility in background (default true)
    eligibilityRefreshInterval?: 'manual' | 'daily' | 'weekly' | 'on_assessment_change'; // default 'on_assessment_change'
  };
  // Crew-admin PREFERENCE layer: a crew admin's opt-out per module (default on when
  // absent). Independent of availability — a crew's "off" here persists even if a
  // Persera grant or tier change later makes the module available again.
  featureFlags?: {
    retroEnabled?: boolean; // Whether Retrospectives are enabled for this crew (default true)
    boardsEnabled?: boolean; // Whether Collab Boards are enabled for this crew (default true)
    mentorshipEnabled?: boolean;
    mentorshipPlansEnabled?: boolean;
    goalsEnabled?: boolean;
    badgesEnabled?: boolean;
    feedsEnabled?: boolean;
    pulseCheckEnabled?: boolean;
    quickPollEnabled?: boolean;
    projectsEnabled?: boolean;
    competencyDefinitionsEnabled?: boolean;
    bulkMemberManagementEnabled?: boolean;
    orgChartEnabled?: boolean;
    performanceReviewsEnabled?: boolean;
  };
  /**
   * Persera (platform-admin) OVERRIDE layer, keyed by CrewModuleId. `true` grants a
   * module to this crew beyond what its tier/crewType plan includes (grant-only —
   * it never removes tier access). Absent = inherit the tier plan. Set only by
   * Persera admins; the resolved on/off is exposed via `moduleAccess`.
   */
  moduleOverrides?: Partial<Record<string, boolean>>;
  /**
   * Server-resolved on/off state per module id (registry tier/crewType gate +
   * manual featureFlags + dependency cascade). Populated by crewToDTO; keyed by
   * CrewModuleId. Typed loosely here to avoid a circular import with
   * crewModules.entities — consumers cast to Record<CrewModuleId, boolean>.
   */
  moduleAccess?: Record<string, boolean>;
  themeConfig?: {
    primaryColor?: string; // Hex color for primary UI elements (buttons, links, active states)
    secondaryColor?: string; // Hex color for secondary/accent UI elements
    backgroundColor?: string; // Hex color for page background
    darkMode?: boolean; // Whether to use dark palette mode
    errorColor?: string; // Hex color for error states and alerts
    warningColor?: string; // Hex color for warning states and alerts
    infoColor?: string; // Hex color for info states and alerts
    successColor?: string; // Hex color for success states and alerts
  };
}

// CrewDTO omits the `teams` array. Teams are served via the paginated
// /teams endpoint (Team.find({ crewSlug })) and the useTeams hook — the crew
// document's teams array is a redundant back-reference and unbounded, so it is
// deliberately not serialized on this frequently-loaded DTO.
export type CrewDTO = Omit<Crew, 'teams'>;
